import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Gift, Loader2, Search, Sparkles, MapPin, Users, ShoppingBag, CalendarDays } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";
import { PrintableGiftCard } from "@/components/PrintableGiftCard";

const STATUS = { active: "Active", partially_used: "Active · partly used", redeemed: "Fully used", voided: "Voided", expired: "Expired" };
const USE = [
  { to: "/offerings", label: "Sessions & services", icon: Sparkles },
  { to: "/events", label: "Classes, workshops & events", icon: CalendarDays },
  { to: "/marketplace", label: "Shop", icon: ShoppingBag },
  { to: "/locations", label: "Our centers", icon: MapPin },
  { to: "/enhancers", label: "Enhancers", icon: Users },
];

const fmtDate = (s) => (s ? new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");

const History = ({ rows }) => (
  <div className="mt-6" data-testid="balance-history">
    <p className="eyw-label mb-2">Card activity</p>
    {rows.length === 0 ? <p className="text-sm text-[#8FA89B]" data-testid="balance-history-empty">Not used yet.</p> : (
      <ul className="divide-y divide-[#F1EDE6] text-sm">
        {rows.map((h, i) => (
          <li key={i} className="flex items-center gap-3 py-2" data-testid={`balance-history-${i}`}>
            <span className="text-[#8FA89B] w-24 shrink-0">{fmtDate(h.at)}</span>
            <span className="flex-1 text-[#1A242B]">{h.label || "Purchase"}</span>
            <span className={h.amount < 0 ? "text-[#3F6B55]" : "text-[#1A242B]"}>{h.amount < 0 ? `+${money(-h.amount)}` : `−${money(h.amount)}`}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const Result = ({ code }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["gift-balance", code], retry: false,
    queryFn: () => api.get(`/gift-cards/check/${encodeURIComponent(code)}`).then((r) => r.data),
  });
  if (isLoading) return <div className="py-8 grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" /></div>;
  if (error) return <p className="text-sm text-red-600 mt-6" data-testid="balance-error">{formatApiError(error.response?.data?.detail)}</p>;
  return (
    <div className="mt-8" data-testid="balance-result">
      <div className="rounded-2xl bg-[#144F75] text-[#F9F7F3] p-6">
        <p className="font-mono text-xs tracking-widest opacity-80" data-testid="balance-code">{data.code}</p>
        <p className="font-serif text-5xl mt-2" data-testid="balance-amount">{money(data.balance)}</p>
        <p className="text-sm opacity-80 mt-1">remaining of {money(data.initial_balance)}</p>
        <div className="flex flex-wrap gap-2 mt-4 text-xs">
          <span className={`rounded-full px-3 py-1 ${data.usable ? "bg-[#5C8A72]" : "bg-[#D98E73]"}`} data-testid="balance-status">{STATUS[data.status] || data.status}</span>
          <span className="rounded-full px-3 py-1 bg-white/10" data-testid="balance-expiry">{data.expires_at ? `${data.status === "expired" ? "Expired" : "Valid until"} ${fmtDate(data.expires_at)}` : "No expiry date"}</span>
        </div>
      </div>
      {data.usable && <PrintableGiftCard card={data} />}
      <History rows={data.history || []} />
    </div>
  );
};

export default function GiftCardBalance() {
  const [params, setParams] = useSearchParams();
  const code = (params.get("code") || "").trim().toUpperCase();
  const [input, setInput] = useState(code);
  const submit = (e) => { e.preventDefault(); if (input.trim()) setParams({ code: input.trim().toUpperCase() }); };
  return (
    <div className="min-h-[80vh] px-6 py-16" data-testid="gift-balance-page">
      <div className="max-w-2xl mx-auto">
        <span className="grid place-items-center h-12 w-12 rounded-full bg-[#D98E73] text-white mb-5"><Gift size={22} /></span>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A242B]">Check your gift card</h1>
        <p className="text-[#4A5D6B] mt-3">Enter the code from your gift card email to see your balance, expiry date and activity.</p>
        <form onSubmit={submit} className="flex gap-2 mt-8">
          <input value={input} onChange={(e) => setInput(e.target.value.toUpperCase())} placeholder="EYW-XXXX-XXXX-XXXX" className="eyw-input flex-1 min-w-0 font-mono text-sm" data-testid="balance-code-input" />
          <button type="submit" disabled={!input.trim()} className="eyw-btn" data-testid="balance-check-btn"><Search size={16} /> Check</button>
        </form>
        {code && <Result code={code} />}
        <div className="mt-10" data-testid="balance-where-to-use">
          <p className="eyw-label mb-3">Where to use it</p>
          <p className="text-sm text-[#4A5D6B] mb-4">Enter your code at checkout online, or share it at any Enhancing Your World front desk. Any unused balance stays on the card.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {USE.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} className="eyw-card p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-transform" data-testid={`balance-use-${to.slice(1)}`}>
                <Icon size={18} className="text-[#144F75]" /> <span className="text-[#1A242B]">{label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
